import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "../api/client";
import { Sparkles, Clock, X, RotateCcw, ChevronRight, Pill } from "lucide-react";

const inr = (v) => `₹${Number(v || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;

const daysFromNow = (iso) => {
    if (!iso) return null;
    const ms = new Date(iso).getTime() - Date.now();
    return Math.round(ms / 86400000);
};

/**
 * RefillPredictionsCard — surfaces upcoming refill predictions for the logged-in user.
 * Renders nothing if no active predictions.
 *
 * Props:
 *  - compact (bool) — show in a thinner format suitable for sidebars
 *  - dense  (bool) — show in a single-row banner format
 */
const RefillPredictionsCard = ({ compact = false, dense = false }) => {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState({});
    const navigate = useNavigate();

    const load = () => {
        setLoading(true);
        api.get("/refill-predictions")
            .then((r) => setItems(r.data.items || []))
            .catch(() => setItems([]))
            .finally(() => setLoading(false));
    };
    useEffect(load, []);

    const snooze = async (id) => {
        setBusy((s) => ({ ...s, [id]: true }));
        try {
            await api.post(`/refill-predictions/${id}/snooze`);
            toast.success("Snoozed for 7 days");
            load();
        } catch {
            toast.error("Could not snooze");
        } finally {
            setBusy((s) => ({ ...s, [id]: false }));
        }
    };

    const dismiss = async (id) => {
        setBusy((s) => ({ ...s, [id]: true }));
        try {
            await api.post(`/refill-predictions/${id}/dismiss`);
            load();
        } catch {
            toast.error("Could not dismiss");
        } finally {
            setBusy((s) => ({ ...s, [id]: false }));
        }
    };

    const orderNow = async (id) => {
        setBusy((s) => ({ ...s, [id]: true }));
        try {
            const { data } = await api.post(`/refill-predictions/${id}/order-now`);
            if (data.prescription_required) {
                toast.message("This is a prescription medicine — pick a verified Rx in cart.");
            }
            navigate(`/products/${data.product.id}`);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Could not start refill");
        } finally {
            setBusy((s) => ({ ...s, [id]: false }));
        }
    };

    if (loading || items.length === 0) return null;

    return (
        <section
            className={`rounded-2xl border border-brand-100 bg-gradient-to-br from-brand-50/70 to-white p-4 dark:from-brand-950/30 dark:to-zinc-900 dark:border-white/10 ${compact ? "" : "p-5"}`}
            data-testid="refill-predictions-card"
        >
            <div className="mb-3 flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-brand-700" />
                <p className="text-xs font-semibold uppercase tracking-wider text-brand-700">Predicted refills</p>
                <span className="rounded-full bg-brand-100 px-2 py-0.5 text-[10px] font-semibold text-brand-700" data-testid="refill-predictions-count">{items.length}</span>
            </div>

            <ul className={dense ? "flex gap-3 overflow-x-auto pb-1" : "space-y-2"} data-testid="refill-predictions-list">
                {items.map((p) => {
                    const prod = p.product_snapshot || {};
                    const daysLeft = daysFromNow(p.expected_refill_date);
                    return (
                        <li
                            key={p.id}
                            className={`group relative rounded-xl border border-zinc-100 bg-white p-3 transition-shadow hover:shadow-md dark:bg-white/[0.03] dark:border-white/10 ${dense ? "w-72 shrink-0" : ""}`}
                            data-testid={`refill-prediction-${p.id}`}
                        >
                            <div className="flex items-start gap-3">
                                {prod.images?.[0] ? (
                                    <img src={prod.images[0]} alt={prod.name} className="h-12 w-12 rounded-md object-cover" />
                                ) : (
                                    <span className="grid h-12 w-12 place-items-center rounded-md bg-brand-50"><Pill className="h-5 w-5 text-brand-600" /></span>
                                )}
                                <div className="min-w-0 flex-1">
                                    <p className="line-clamp-1 text-sm font-medium text-zinc-900 dark:text-zinc-50">{prod.name}</p>
                                    <p className="text-[11px] text-zinc-500">
                                        {prod.brand}{prod.pack_size ? ` · ${prod.pack_size}` : ""} · {inr(prod.selling_price)}
                                    </p>
                                    <p className="mt-1 inline-flex items-center gap-1 text-[11px] font-medium text-amber-700">
                                        <Clock className="h-3 w-3" />
                                        {daysLeft == null ? "—" : daysLeft <= 0 ? "Likely needed today" : daysLeft === 1 ? "In ~1 day" : `In ~${daysLeft} days`}
                                    </p>
                                </div>
                            </div>
                            <div className="mt-3 flex items-center gap-1.5">
                                <button
                                    onClick={() => orderNow(p.id)}
                                    disabled={busy[p.id]}
                                    className="inline-flex items-center gap-1 rounded-md bg-brand-700 px-2.5 py-1.5 text-[11px] font-medium text-white hover:bg-brand-800 disabled:opacity-50"
                                    data-testid={`refill-order-now-${p.id}`}
                                >
                                    Order now <ChevronRight className="h-3 w-3" />
                                </button>
                                <button
                                    onClick={() => snooze(p.id)}
                                    disabled={busy[p.id]}
                                    title="Snooze 7 days"
                                    className="inline-flex items-center gap-1 rounded-md border border-zinc-200 bg-white px-2 py-1.5 text-[11px] text-zinc-700 hover:bg-zinc-50"
                                    data-testid={`refill-snooze-${p.id}`}
                                >
                                    <RotateCcw className="h-3 w-3" /> 7d
                                </button>
                                <button
                                    onClick={() => dismiss(p.id)}
                                    disabled={busy[p.id]}
                                    title="Dismiss"
                                    className="inline-flex h-7 w-7 items-center justify-center rounded-md text-zinc-400 hover:bg-zinc-100 hover:text-zinc-700"
                                    data-testid={`refill-dismiss-${p.id}`}
                                >
                                    <X className="h-3 w-3" />
                                </button>
                            </div>
                        </li>
                    );
                })}
            </ul>
        </section>
    );
};

export default RefillPredictionsCard;
